import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useVerifySwachhataCertificateMutation } from "../redux/auth/authApi";
import { BADGE_META, CAMPAIGN } from "../Components/SchoolAdmin/Swachhata/swachhataContent";
import { Leaves } from "../Components/SchoolAdmin/Swachhata/SwachhataBits";
import ecoLogo from "../assets/images/Logo-2.png";
import "../Components/SchoolAdmin/Swachhata/Swachhata.css";

/*
 * /swachhata-hi-seva/verify[/:certId] — public certificate check. The QR on
 * every Swachhata Hi Seva certificate (school or student) opens this page with
 * the id filled in; anyone can also type an id by hand.
 */
const CERT_RX = /^SHS\d{2}-(SCH|STU)-[A-Z2-9]{7}$/;
const clean = (v) => String(v || "").replace(/\s+/g, "").toUpperCase();
const errMsg = (e, fb) => e?.data?.message || e?.error?.data?.message || e?.message || fb;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" }) : "—");

export default function SwachhataVerify() {
  const { certId } = useParams();
  const navigate = useNavigate();
  const [value, setValue] = useState(certId || "");
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [verify, { isLoading }] = useVerifySwachhataCertificateMutation();

  useEffect(() => { document.title = `Verify certificate · ${CAMPAIGN.name} — ${CAMPAIGN.programme}`; }, []);

  const run = useCallback(async (raw) => {
    const id = clean(raw);
    setErr("");
    setResult(null);
    if (!CERT_RX.test(id)) {
      setErr("Enter a valid certificate ID — it looks like SHS26-STU-ABC2345 and is printed at the bottom of the certificate.");
      return;
    }
    try {
      const res = await verify({ certificateId: id }).unwrap();
      setResult({ ...res, id });
    } catch (e) {
      setErr(errMsg(e, "Could not verify the certificate right now. Please try again."));
    }
  }, [verify]);

  useEffect(() => {
    if (certId) { setValue(certId); run(certId); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [certId]);

  const submit = (e) => {
    e.preventDefault();
    const id = clean(value);
    if (id && id !== clean(certId)) navigate(`/swachhata-hi-seva/verify/${id}`, { replace: !!certId });
    else run(id);
  };

  const c = result?.found ? result.certificate : null;
  const badge = c?.badge ? BADGE_META[c.badge] : null;

  return (
    <div className="shs-page">
      <Leaves />
      <div className="shs-shell shs-shell--welcome" style={{ height: "auto" }}>
        <div className="shs-intro">
          <span className="shs-badge-pill"><img src={ecoLogo} alt="" />{`Certificate verification · ${CAMPAIGN.name}`}</span>
          <h2 className="shs-slide__title" style={{ marginTop: 14 }}>Verify a Swachhata Hi Seva certificate</h2>
          <p className="shs-slide__lead">
            {"Every school and student participation certificate carries a unique ID and a QR code. Enter the ID below (or scan the QR) to confirm it was issued by the Eco Clubs for Mission LiFE portal."}
          </p>

          <form className="shs-form" onSubmit={submit} noValidate style={{ marginTop: 18 }}>
            <div className="shs-form__grid" style={{ gridTemplateColumns: "1fr" }}>
              <label className={`shs-field ${err ? "is-error" : ""}`}>
                <span className="shs-field__label">Certificate ID</span>
                <input
                  type="text" value={value} maxLength={24} placeholder="e.g. SHS26-STU-ABC2345" autoCapitalize="characters" spellCheck={false}
                  onChange={(e) => setValue(e.target.value.toUpperCase())}
                  style={{ fontFamily: "Outfit, Inter, sans-serif", letterSpacing: 1.5, fontWeight: 700 }}
                />
                {err && <small className="shs-field__err">{err}</small>}
              </label>
            </div>
            <div className="shs-form__actions">
              <button type="submit" className="shs-btn shs-btn--primary shs-btn--lg" disabled={isLoading}>
                {isLoading ? "Checking…" : "Verify certificate"} <span className="shs-btn__arrow">→</span>
              </button>
              <Link to="/swachhata-hi-seva" className="shs-btn shs-btn--ghost">Take the student quiz</Link>
            </div>
          </form>

          {result && !result.found && (
            <div className="shs-school is-missing" style={{ marginTop: 22 }}>
              <span className="shs-school__icon">❌</span>
              <div>
                <b>{`No certificate found for ${result.id}`}</b>
                <small>{"Check the ID printed on the certificate. A genuine certificate always verifies here."}</small>
              </div>
            </div>
          )}

          {c && (
            <div className="shs-school is-found" style={{ marginTop: 22, alignItems: "flex-start" }}>
              <span className="shs-school__icon">✅</span>
              <div style={{ flex: 1 }}>
                <b>{`Genuine certificate · ${c.kind === "school" ? "School participation" : "Student participation"}`}</b>
                <small>{`${c.campaign} · ${CAMPAIGN.programme}`}</small>
                <div className="shs-verify__grid">
                  <div><span>{c.kind === "school" ? "School" : "Student"}</span><b>{c.name || "—"}</b></div>
                  {c.kind !== "school" && <div><span>Class</span><b>{c.class ? `Class ${c.class}` : "—"}</b></div>}
                  {c.kind !== "school" && <div><span>School</span><b>{c.school?.school_name || `UDISE ${c.udise_id}`}</b></div>}
                  <div><span>UDISE</span><b>{c.udise_id || "—"}</b></div>
                  <div><span>Location</span><b>{[c.school?.district, c.school?.state].filter(Boolean).join(", ") || "—"}</b></div>
                  {/* school certificates come from the SWM report form, not a quiz */}
                  {c.kind !== "school" && <div><span>Quiz score</span><b>{c.score != null ? `${c.score} / ${c.total} (${c.percent}%)` : "—"}</b></div>}
                  {c.kind !== "school" && <div><span>Badge</span><b>{badge ? `${badge.emoji} ${badge.label}` : c.badgeLabel || "—"}</b></div>}
                  <div><span>Issued on</span><b>{fmtDate(c.issuedAt)}</b></div>
                  <div><span>Certificate ID</span><b style={{ fontFamily: "Outfit, Inter, sans-serif", letterSpacing: 1 }}>{c.id}</b></div>
                </div>
              </div>
            </div>
          )}
          <div className="shs-welcome__meta" style={{ marginTop: 22 }}>{CAMPAIGN.ministry}</div>
        </div>
      </div>
    </div>
  );
}
